import { AddressAutocompleteInput } from '../../components/common/AddressAutocompleteInput';
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  Option,
  Textarea,
  Checkbox,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepIndicator,
  Grid,
  FormHelperText,
  Divider,
} from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { AffiliatesAPI } from '../../lib/apiAffiliates';
import { apiClient } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { applyPrefill } from '../../utils/prefill';
import type { AffiliateApplicationFormData } from '../../types';

/** The one identity field /api/users/current/ has that the auth payload lacks. */
interface CurrentUserProfile {
  mobile_phone?: string;
}

export const AffiliateSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [w9File, setW9File] = useState<File | null>(null);
  
  const [formData, setFormData] = useState<AffiliateApplicationFormData>({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    business_type: 'individual',
    business_name: '',
    business_ein: '',
    tax_id: '',
    bank_account_holder_name: '',
    bank_routing_number: '',
    bank_account_number: '',
    bank_account_type: 'checking',
    how_did_you_hear: '',
    marketing_experience: '',
    expected_referrals_per_month: 0,
    terms_accepted: false,
  });

  // The phone lives on the User record, not in the auth payload. Asked for by
  // identity rather than picked out of /api/users-list/, which pages at 20 and
  // would quietly return nothing for anyone further down a big org.
  const { data: currentUser } = useQuery({
    queryKey: ['currentUserProfile'],
    queryFn: async () => {
      const response = await apiClient.get<CurrentUserProfile>('/api/users/current/');
      return response.data;
    },
    enabled: !!user,
  });

  // Existing users are redirected here by AffiliateRoute, so ApexOS already
  // knows who they are - only an anonymous applicant should face a blank
  // identity step. Seeded, never forced: every field stays editable.
  useEffect(() => {
    if (!user) return;
    setFormData(prev =>
      applyPrefill(prev, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        phone: currentUser?.mobile_phone,
      })
    );
  }, [user, currentUser]);

  const steps = [
    'Personal Information',
    'Address',
    'Business Details',
    'Banking Information',
    'Marketing & Documents',
    'Review & Submit',
  ];

  const handleInputChange = (field: keyof AffiliateApplicationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleNext = () => {
    // Basic validation for each step
    if (!validateCurrentStep()) {
      return;
    }
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const validateCurrentStep = (): boolean => {
    switch (activeStep) {
      case 0: // Personal Information
        if (!formData.email || !formData.first_name || !formData.last_name || !formData.phone) {
          setError('Please fill in all required fields');
          return false;
        }
        if (!formData.email.includes('@')) {
          setError('Please enter a valid email address');
          return false;
        }
        break;
      case 1: // Address
        if (!formData.address_line_1 || !formData.city || !formData.state || !formData.zip_code) {
          setError('Please fill in all required address fields');
          return false;
        }
        break;
      case 2: // Business Details
        if (formData.business_type !== 'individual' && !formData.business_name) {
          setError('Business name is required for non-individual applications');
          return false;
        }
        break;
      case 3: // Banking Information
        // Banking information is now optional - can be added later
        if (formData.bank_routing_number && formData.bank_routing_number.length !== 9) {
          setError('Routing number must be 9 digits');
          return false;
        }
        break;
      case 4: // Marketing & Documents
        // W9 is now optional - can be uploaded later
        if (formData.expected_referrals_per_month < 0) {
          setError('Expected referrals must be 0 or greater');
          return false;
        }
        break;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!formData.terms_accepted) {
      setError('You must accept the terms and conditions');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await AffiliatesAPI.submitApplication(formData, w9File || undefined);
      // Success - show success message and redirect
      navigate('/affiliate-application-success');
    } catch (err: any) {
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Personal Information
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h3">Personal Information</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Please provide your personal contact information
            </Typography>
            
            <FormControl required>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your.email@example.com"
                autoComplete="off"
              />
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    placeholder="John"
                    autoComplete="off"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    placeholder="Doe"
                    autoComplete="off"
                  />
                </FormControl>
              </Grid>
            </Grid>

            <FormControl required>
              <FormLabel>Phone Number</FormLabel>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                autoComplete="off"
              />
            </FormControl>
          </Box>
        );

      case 1: // Address
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h3">Address Information</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Your business or personal address for tax purposes
            </Typography>

            <FormControl required>
              <FormLabel>Address Line 1</FormLabel>
              <AddressAutocompleteInput
                value={formData.address_line_1}
                onChange={(value) => handleInputChange('address_line_1', value)}
                onAddressSelect={(parsed) => setFormData(previous => ({ ...previous, address_line_1: parsed.address_1, city: parsed.city, state: parsed.state, zip_code: parsed.zip_code }))}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Address Line 2</FormLabel>
              <Input
                value={formData.address_line_2}
                onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                placeholder="Apt, Suite, Unit, etc. (optional)"
                autoComplete="off"
              />
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>City</FormLabel>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="New York"
                    autoComplete="off"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>State</FormLabel>
                  <Input
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="NY"
                    autoComplete="off"
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>ZIP Code</FormLabel>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => handleInputChange('zip_code', e.target.value)}
                    placeholder="10001"
                    autoComplete="off"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Country</FormLabel>
                  <Input
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    disabled
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 2: // Business Details
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h3">Business Information</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Your business structure
            </Typography>

            <FormControl required>
              <FormLabel>Business Type</FormLabel>
              <Select
                value={formData.business_type}
                onChange={(e, newValue) => handleInputChange('business_type', newValue)}
              >
                <Option value="individual">Individual</Option>
                <Option value="sole_proprietorship">Sole Proprietorship</Option>
                <Option value="llc">LLC</Option>
                <Option value="corporation">Corporation</Option>
                <Option value="partnership">Partnership</Option>
              </Select>
            </FormControl>

            {formData.business_type !== 'individual' && (
              <FormControl required>
                <FormLabel>Business Name</FormLabel>
                <Input
                  value={formData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  placeholder="Your Company Name LLC"
                  autoComplete="off"
                />
              </FormControl>
            )}

            <Alert color="neutral" sx={{ mt: 2 }}>
              ℹ️ Tax identification (SSN for individuals, EIN for businesses) will be obtained from your W-9 form.
            </Alert>
          </Box>
        );

      case 3: // Banking Information
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h3">Banking Information (Optional)</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Where we'll send your commission payments via ACH transfer
            </Typography>

            <Alert color="primary" sx={{ mb: 2 }}>
              🔒 Your banking information is encrypted and stored securely. We use bank-level security to protect your data.
            </Alert>

            <Alert color="warning" sx={{ mb: 2 }}>
              ℹ️ You can skip this section now and add your banking information later from your Affiliate Dashboard. 
              However, banking information is required before we can process any commission payments.
            </Alert>

            <FormControl>
              <FormLabel>Account Holder Name (Optional)</FormLabel>
              <Input
                value={formData.bank_account_holder_name}
                onChange={(e) => handleInputChange('bank_account_holder_name', e.target.value)}
                placeholder="Name as it appears on your bank account"
                autoComplete="off"
                slotProps={{
                  input: { 
                    'data-lpignore': 'true',
                    'data-form-type': 'other'
                  }
                }}
              />
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Routing Number (Optional)</FormLabel>
                  <Input
                    value={formData.bank_routing_number}
                    onChange={(e) => handleInputChange('bank_routing_number', e.target.value)}
                    placeholder="9 digits"
                    autoComplete="off"
                    slotProps={{
                      input: { 
                        maxLength: 9,
                        'data-lpignore': 'true',
                        'data-form-type': 'other'
                      }
                    }}
                  />
                  <FormHelperText>9-digit routing number</FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Account Number (Optional)</FormLabel>
                  <Input
                    value={formData.bank_account_number}
                    onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                    placeholder="Your account number"
                    type="password"
                    autoComplete="new-password"
                    slotProps={{
                      input: { 
                        'data-lpignore': 'true',
                        'data-form-type': 'other'
                      }
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <FormControl>
              <FormLabel>Account Type (Optional)</FormLabel>
              <Select
                value={formData.bank_account_type}
                onChange={(e, newValue) => handleInputChange('bank_account_type', newValue)}
              >
                <Option value="checking">Checking</Option>
                <Option value="savings">Savings</Option>
              </Select>
            </FormControl>
          </Box>
        );

      case 4: // Marketing & Documents
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h3">Marketing Information & Documents</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Help us understand your marketing approach and upload required documents
            </Typography>

            <FormControl>
              <FormLabel>How did you hear about our affiliate program?</FormLabel>
              <Textarea
                value={formData.how_did_you_hear}
                onChange={(e) => handleInputChange('how_did_you_hear', e.target.value)}
                placeholder="Please tell us how you discovered our affiliate program"
                minRows={2}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Marketing Experience</FormLabel>
              <Textarea
                value={formData.marketing_experience}
                onChange={(e) => handleInputChange('marketing_experience', e.target.value)}
                placeholder="Describe your marketing experience and strategy for promoting ApexOS"
                minRows={3}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Expected Referrals Per Month</FormLabel>
              <Input
                value={formData.expected_referrals_per_month}
                onChange={(e) => handleInputChange('expected_referrals_per_month', parseInt(e.target.value) || 0)}
                placeholder="0"
                autoComplete="off"
                slotProps={{
                  input: { 
                    type: 'number',
                    min: 0 
                  }
                }}
              />
              <FormHelperText>Estimate how many referrals you expect to generate monthly</FormHelperText>
            </FormControl>

            <Divider />

            <FormControl>
              <FormLabel>W-9 Tax Form (Optional - Can Be Added Later)</FormLabel>
              <Alert color="neutral" sx={{ mb: 2 }}>
                ℹ️ You can skip uploading your W-9 now and add it later from your Affiliate Dashboard.
                However, a W-9 is required before we can process any commission payments.
              </Alert>
              <Box sx={{ 
                border: '2px dashed', 
                borderColor: 'divider', 
                borderRadius: 'sm', 
                p: 3, 
                textAlign: 'center',
                backgroundColor: 'background.level1'
              }}>
                {w9File ? (
                  <Box>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      📎 {w9File.name}
                    </Typography>
                    <Button 
                      size="sm" 
                      variant="outlined"
            color="primary" 
                      onClick={() => setW9File(null)}
                    >
                      Remove File
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Typography level="body-sm" sx={{ mb: 2 }}>
                      Upload your completed W-9 form (PDF format preferred)
                    </Typography>
                    <Button
                      component="label"
                      variant="outlined"
                      color="primary"
                      size="sm"
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setW9File(e.target.files[0]);
                          }
                        }}
                      />
                    </Button>
                  </>
                )}
              </Box>
              <FormHelperText>
                Required for tax reporting purposes before payments can be processed. Download a blank W-9 form{' '}
                <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer">
                  here
                </a>
              </FormHelperText>
            </FormControl>
          </Box>
        );

      case 5: // Review & Submit
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h3">Review Your Application</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Please review your information before submitting
            </Typography>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Personal Information</Typography>
                <Typography level="body-sm">
                  {formData.first_name} {formData.last_name}<br />
                  {formData.email}<br />
                  {formData.phone}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Address</Typography>
                <Typography level="body-sm">
                  {formData.address_line_1}<br />
                  {formData.address_line_2 && <>{formData.address_line_2}<br /></>}
                  {formData.city}, {formData.state} {formData.zip_code}<br />
                  {formData.country}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Business Information</Typography>
                <Typography level="body-sm">
                  Business Type: {formData.business_type.replace('_', ' ').toUpperCase()}<br />
                  {formData.business_name && <>Business Name: {formData.business_name}</>}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Banking Information</Typography>
                <Typography level="body-sm">
                  {formData.bank_account_holder_name && <>Account Holder: {formData.bank_account_holder_name}<br /></>}
                  {formData.bank_routing_number && <>Routing Number: ****{formData.bank_routing_number.slice(-4)}<br /></>}
                  {formData.bank_account_type && <>Account Type: {formData.bank_account_type.toUpperCase()}</>}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Documents</Typography>
                <Typography level="body-sm">
                  W-9 Form: {w9File ? `✅ ${w9File.name}` : '❌ Not uploaded'}
                </Typography>
              </CardContent>
            </Card>

            <Divider />

            <Card variant="soft" color="primary">
              <CardContent>
                <Typography level="title-sm" sx={{ mb: 2 }}>Terms and Conditions</Typography>
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  By joining the ApexOS Affiliate Program, you agree to:
                </Typography>
                <ul style={{ marginLeft: '20px' }}>
                  <li><Typography level="body-sm">Promote ApexOS ethically and in accordance with our brand guidelines</Typography></li>
                  <li><Typography level="body-sm">Provide accurate information in your application</Typography></li>
                  <li><Typography level="body-sm">Comply with all applicable laws and regulations</Typography></li>
                  <li><Typography level="body-sm">Accept the 20% commission rate on referred subscriptions</Typography></li>
                  <li><Typography level="body-sm">Maintain confidentiality of any proprietary information</Typography></li>
                </ul>
                
                <Checkbox
                  checked={formData.terms_accepted}
                  onChange={(e) => handleInputChange('terms_accepted', e.target.checked)}
                  label="I accept the terms and conditions of the ApexOS Affiliate Program"
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Card>
        <CardContent>
          <Typography level="h2" sx={{ mb: 1, textAlign: 'center' }}>
            🤝 ApexOS Affiliate Program Application
          </Typography>
          <Typography level="body-md" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
            Join our affiliate program and earn 20% commission on every referral
          </Typography>

          <Stepper sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step
                key={step}
                indicator={
                  <StepIndicator
                    variant={index <= activeStep ? 'solid' : 'outlined'}
                    color={index < activeStep ? 'success' : index === activeStep ? 'primary' : 'neutral'}
                  >
                    {index < activeStep ? '✓' : index + 1}
                  </StepIndicator>
                }
                sx={{
                  '&::after': {
                    backgroundColor: index < activeStep ? 'success.500' : 'divider',
                  },
                }}
              >
                <Typography level="body-xs">{step}</Typography>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert color="danger" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0 || isSubmitting}
            >
              Back
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="solid"
                color="success"
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.terms_accepted}
                startDecorator={isSubmitting && <CircularProgress size="sm" />}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            ) : (
              <Button
                variant="solid"
                color="primary"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};